import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AlertEntry, AlertEvent, AlertManager, AlertType } from "@/types/common";
import "./Alert.css";

type Theme = "dark" | "light";
type Variant = "solid" | "outlined";
type Position =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center";
type Direction = "ltr" | "rtl";

export interface AlertProps {
  theme?: Theme;
  variant?: Variant;
  position?: Position;
  className?: string;
  direction?: Direction;
  alertManager?: AlertManager;
  handleSingleDismiss?: (id: string) => void;
  handleAllDismissed?: () => void;
  dataTestId?: string;
  gaComponent?: string;
}

const DEFAULT_THEME: Theme = "dark";
const DEFAULT_VARIANT: Variant = "solid";
const DEFAULT_POSITION: Position = "top-right";
const DEFAULT_DIRECTION: Direction = "ltr";

function isDismissEvent(event: AlertEvent): event is { id: string; dismiss: boolean } {
  return typeof event === "object" && event !== null && "dismiss" in event;
}

function getStatusIcon(type: AlertType) {
  if (type === "success") {
    return (
      <span
        className="custom-alert__statusIcon custom-alert__statusIcon--success"
        aria-hidden="true"
        data-testid="icon-checkmark"
      >
        ✓
      </span>
    );
  }

  if (type === "error") {
    return (
      <span
        className="custom-alert__statusIcon custom-alert__statusIcon--error"
        aria-hidden="true"
        data-testid="icon-close"
      >
        !
      </span>
    );
  }

  return null;
}

export default function Alert({
  theme = DEFAULT_THEME,
  variant = DEFAULT_VARIANT,
  position = DEFAULT_POSITION,
  className,
  direction = DEFAULT_DIRECTION,
  alertManager,
  handleSingleDismiss,
  handleAllDismissed,
  dataTestId,
}: AlertProps) {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<AlertEntry[]>([]);

  useEffect(() => {
    if (!alertManager) {
      setAlerts([]);
      return;
    }

    setAlerts(alertManager.getAlerts());

    const unsubscribe = alertManager.subscribe((event) => {
      setAlerts((prev) => {
        if (isDismissEvent(event)) {
          return prev.filter((item) => item.id !== event.id);
        }

        const nextItem: AlertEntry = event;
        const idx = prev.findIndex((item) => item.id === event.id);

        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...nextItem };
          return next;
        }

        return [...prev, nextItem];
      });
    });

    return unsubscribe;
  }, [alertManager]);

  const rootClassName = useMemo(() => {
    const classes = [
      "custom-alert",
      `custom-alert--${theme}`,
      `custom-alert--${variant}`,
      `custom-alert--${position}`,
      className,
    ];

    return classes.filter(Boolean).join(" ");
  }, [theme, variant, position, className]);

  const dismissAlert = (id: string) => {
    alertManager?.dismiss(id);
    setAlerts((prev) => prev.filter((item) => item.id !== id));
    handleSingleDismiss?.(id);
  };

  useEffect(() => {
    if (alerts.length === 0) {
      handleAllDismissed?.();
    }
  }, [alerts.length, handleAllDismissed]);

  if (alerts.length === 0) {
    return null;
  }

  return (
    <ul className={rootClassName} dir={direction} data-testid={dataTestId} role="region" aria-live="polite">
      {alerts.map((item) => {
        const id = item.id;
        const type = item.type ?? "neutral";
        const statusIcon = getStatusIcon(type);

        return (
          <li key={id} className={`custom-alert__item custom-alert__item--${type}`}>
            {statusIcon}
            <div className="custom-alert__content">
              {item.title ? <p className="custom-alert__title">{item.title}</p> : null}
              {item.description ? <p className="custom-alert__description">{item.description}</p> : null}
            </div>
            <button
              type="button"
              className="custom-alert__close"
              onClick={() => dismissAlert(item.id)}
              aria-label={t("alerts.dismiss")}
            >
              ×
            </button>
          </li>
        );
      })}
    </ul>
  );
}